import { Fragment } from 'react';
import { Check, Plane, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { formatFlightTime } from '@/lib/disruptions/airport-tz';
import { fmtDuration } from '@/routes/disruptions/pnr-status';
import { AirportCode } from './airport-ui';

// The hero's booked-itinerary band, drawn the way an airport draws a route: each
// airport ONCE as a board-sized IATA code, joined by a rail with the aircraft
// travelling along it, the flight number on a tag above the rail and the
// disrupted leg struck through in red. Codes and rails sit on ONE centred track
// row; every caption floats above or below (absolute) so nothing can knock the
// track out of alignment. The re-accommodation detail (new flight, date, seat,
// actions) lives in the ReaccommodationDiagram — NOT duplicated here.

/** A booked leg of the original itinerary. */
export interface JourneyLeg {
  flightNumber?: string | undefined;
  origin?: string | undefined;
  destination?: string | undefined;
  /** Scheduled departure (ISO-8601 UTC) when the manifest carried it — shown under
   *  the rail so the operator sees the onward connection's time. */
  scheduledDeparture?: string | undefined;
}

/** Onward-connection feasibility for the SELECTED re-accommodation — whether the new
 *  hub arrival still makes the PNR's surviving onward leg, and when. Drives the
 *  feasibility verdict line under the "protect to final" caption. */
export interface JourneyConnection {
  onwardFlightNumber?: string | undefined;
  onwardOrigin?: string | undefined;
  onwardDepartureUtc?: string | undefined;
  layoverMinutes?: number | undefined;
  connectionFeasible?: boolean | undefined;
  overnightAtHub?: boolean | undefined;
}

interface JourneyRibbonProps {
  /** The booked legs (≥1). The disrupted one is flagged via `disruptedIdx`. */
  legs: JourneyLeg[];
  /** Final destination code (protect-to-final for connections). */
  finalDest?: string | null | undefined;
  /** Whether the booked itinerary is a multi-leg connection. */
  isConnection: boolean;
  /** Index of the disrupted leg within `legs` (-1 when none matched). */
  disruptedIdx: number;
  /** Selected re-accommodation's onward-connection feasibility (when known). */
  connection?: JourneyConnection | null | undefined;
}

/** One airport on the track — the code is the node; its caption floats below. */
function Airport({
  code,
  caption,
  testId,
}: {
  code: string;
  caption?: string | undefined;
  testId?: string | undefined;
}) {
  return (
    <span className="relative flex shrink-0 items-center" data-testid={testId}>
      <AirportCode code={code} size="md" />
      {caption ? (
        <span className="absolute -bottom-[18px] left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {caption}
        </span>
      ) : null}
    </span>
  );
}

/** The rail between two airports: flight-number tag above, aircraft on the line,
 *  the "Disrupted" tag (or the scheduled time) below. */
function Rail({
  flight,
  disrupted,
  time,
}: {
  flight?: string | undefined;
  disrupted: boolean;
  time?: string | undefined;
}) {
  const { t } = useTranslation('pnr');
  return (
    <span
      className="relative flex min-w-[3rem] flex-1 items-center"
      data-testid={disrupted ? 'journey-disrupted' : undefined}
    >
      {flight ? (
        <span
          className={cn(
            'absolute -top-[19px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none tracking-tight ring-1 ring-inset',
            disrupted
              ? 'bg-rose-100 text-rose-700 line-through ring-rose-600/20 dark:bg-rose-900/40 dark:text-rose-300 dark:ring-rose-400/20'
              : 'bg-muted text-muted-foreground ring-border',
          )}
        >
          {flight}
        </span>
      ) : null}

      <span
        className={cn(
          'h-[3px] flex-1 rounded-full',
          disrupted ? 'bg-rose-400 dark:bg-rose-500/70' : 'bg-border',
        )}
      />
      <Plane
        className={cn(
          '-ml-0.5 h-4 w-4 shrink-0 rotate-45',
          disrupted ? 'text-rose-500' : 'text-muted-foreground/70',
        )}
        aria-hidden
      />

      {disrupted ? (
        <span className="absolute -bottom-[18px] left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.12em] text-rose-600 dark:text-rose-400">
          {t('journey.disrupted')}
        </span>
      ) : time ? (
        <span
          className="absolute -bottom-[18px] left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] tabular-nums text-muted-foreground"
          data-testid="journey-leg-time"
        >
          {time}
        </span>
      ) : null}
    </span>
  );
}

export function JourneyRibbon({
  legs,
  finalDest,
  isConnection,
  disruptedIdx,
  connection,
}: JourneyRibbonProps) {
  const { t } = useTranslation('pnr');
  // Deduplicated node path: [origin, dest, dest, …] — each rail i carries legs[i].
  const nodes =
    legs.length > 0 ? [legs[0]?.origin ?? '?', ...legs.map((l) => l.destination ?? '?')] : [];
  const lastIdx = nodes.length - 1;
  const legTime = (leg?: JourneyLeg): string | undefined =>
    leg?.scheduledDeparture ? formatFlightTime(leg.scheduledDeparture, leg.origin) : undefined;

  return (
    <div data-testid="journey-ribbon">
      {/* One centred track row; captions float above/below so alignment holds. */}
      <div
        className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/25 px-5 py-6"
        data-testid="journey-booked"
      >
        {nodes.map((code, i) => (
          <Fragment key={`${code}-${i}`}>
            {i > 0 ? (
              <Rail
                flight={legs[i - 1]?.flightNumber}
                disrupted={i - 1 === disruptedIdx}
                time={legTime(legs[i - 1])}
              />
            ) : null}
            <Airport
              code={code}
              caption={
                i === 0
                  ? t('journey.from')
                  : i === lastIdx
                    ? finalDest === code
                      ? t('journey.final')
                      : t('journey.to')
                    : undefined
              }
              {...(i === lastIdx && finalDest === code ? { testId: 'journey-final-dest' } : {})}
            />
          </Fragment>
        ))}
        {finalDest && nodes[lastIdx] !== finalDest ? (
          <>
            <Rail disrupted={false} />
            <Airport code={finalDest} caption={t('journey.final')} testId="journey-final-dest" />
          </>
        ) : null}
      </div>

      {isConnection && finalDest ? (
        <p className="mt-2 text-[11px] text-muted-foreground">
          {t('journey.protectToFinal', { code: finalDest })}
        </p>
      ) : null}

      {connection ? <ConnectionVerdict connection={connection} /> : null}
    </div>
  );
}

/** The "can the pax still make their onward leg, and when" verdict for the SELECTED
 *  re-accommodation — the answer the operator was missing. */
function ConnectionVerdict({ connection: c }: { connection: JourneyConnection }) {
  const { t } = useTranslation('pnr');
  const when = c.onwardDepartureUtc
    ? formatFlightTime(c.onwardDepartureUtc, c.onwardOrigin)
    : undefined;
  const layover =
    typeof c.layoverMinutes === 'number' ? fmtDuration(c.layoverMinutes * 60_000) : undefined;
  const onward = c.onwardFlightNumber
    ? `${c.onwardFlightNumber}${when ? ` ${when}` : ''}`
    : t('journey.onwardFallback');

  // Feasibility unknown (onward schedule absent — mock manifest): say so plainly.
  if (c.connectionFeasible === undefined) {
    return (
      <p className="mt-1 text-[11px] text-muted-foreground" data-testid="journey-connection-verdict">
        {t('journey.notValidated', { onward })}
      </p>
    );
  }

  const ok = c.connectionFeasible;
  return (
    <p
      className={cn(
        'mt-1 flex flex-wrap items-center gap-1 text-[11px] font-medium',
        ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
      )}
      data-testid="journey-connection-verdict"
    >
      {ok ? (
        <Check className="h-3 w-3 shrink-0" aria-hidden />
      ) : (
        <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden />
      )}
      {ok ? (
        <span>
          {t('journey.connectsTo', { onward })}
          {layover ? t('journey.layoverSuffix', { layover }) : ''}
          {c.onwardOrigin ? t('journey.atSuffix', { station: c.onwardOrigin }) : ''}
          {c.overnightAtHub ? t('journey.overnightSuffix') : ''}
        </span>
      ) : (
        <span>
          {t('journey.misses', { onward })}
          {c.onwardOrigin ? t('journey.atSuffix', { station: c.onwardOrigin }) : ''}
          {c.overnightAtHub ? t('journey.overnightAtHubSuffix') : t('journey.tightSuffix')}
        </span>
      )}
    </p>
  );
}
